
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Server,
  Database,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  RefreshCw
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Define a type matching our new backend metrics structure:
type MetricRow = {
  name: string;
  value: number | null;
  unit: string;
  threshold: number;
  description: string;
};

const SystemHealthMonitor: React.FC = () => {
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Fetch real metrics from new Supabase function
  const { data: metrics, isLoading, refetch } = useQuery({
    queryKey: ['system-health-backend'],
    queryFn: async (): Promise<MetricRow[]> => {
      const { data, error } = await supabase.rpc('get_system_health_metrics');
      if (error) throw error;
      return data || [];
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Status logic based on all metrics (backend structure is flexible)
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-sky-500" />;
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Activity className="h-4 w-4 text-black" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-white text-green-800';
      case 'warning':
        return 'bg-white text-black';
      case 'critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-white text-black';
    }
  };

  // Evaluate status for each metric relative to threshold
  const determineStatus = (metric: MetricRow) => {
    if (metric.value === null || metric.value === undefined) return 'warning';
    if (metric.name.toLowerCase().includes('error') && metric.value >= metric.threshold) return 'critical';
    if (metric.value > metric.threshold) return 'warning';
    return 'healthy';
  };

  // Overall panel status aggregation
  const overallStatus =
    metrics?.some((m) => determineStatus(m) === 'critical') ? 'critical'
      : metrics?.some((m) => determineStatus(m) === 'warning') ? 'warning'
      : 'healthy';

  const handleRefresh = () => {
    refetch();
    setLastUpdated(new Date());
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-white rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // For mini-cards below, quick lookup by metric name
  const dbMetric = metrics?.find(m => m.name === 'Database Response Time');
  const apiMetric = metrics?.find(m => m.name === 'API Latency');
  const errorMetric = metrics?.find(m => m.name === 'Error Rate');

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              System Health Overview
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(overallStatus)}>
                {overallStatus.toUpperCase()}
              </Badge>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
          <p className="text-sm text-black">
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {metrics?.map((metric, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-4 border rounded-lg ${metric.value === null ? 'opacity-70' : ''}`}
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(determineStatus(metric))}
                <div>
                  <h4 className="font-medium">{metric.name}</h4>
                  <p className="text-sm text-black">{metric.description}</p>
                  {metric.value === null && (
                    <span className="text-xs text-black bg-white px-2 py-0.5 rounded">
                      Not available on this system
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold">
                  {metric.value !== null ? `${metric.value}${metric.unit}` : '--'}
                </div>
                <div className="text-sm text-black">
                  Threshold: {metric.threshold}
                  {metric.unit}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Compact quick health cards for core metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Database className="h-4 w-4" />
              Database Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Response Time</span>
                <span>{dbMetric?.value !== null ? `${dbMetric?.value}ms` : '--'}</span>
              </div>
              <Progress
                value={
                  dbMetric?.value !== null && dbMetric?.value !== undefined ?
                    Math.min((dbMetric.value / (dbMetric.threshold || 1)) * 100, 100)
                    : 0
                }
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Wifi className="h-4 w-4" />
              API Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Latency</span>
                <span>
                  {apiMetric && apiMetric.value !== null ? `${apiMetric.value}ms` : '--'}
                </span>
              </div>
              <Progress
                value={
                  apiMetric?.value !== null && apiMetric?.value !== undefined && apiMetric.threshold ?
                    Math.min((apiMetric.value / apiMetric.threshold) * 100, 100) : 0
                }
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="h-4 w-4" />
              Error Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Errors</span>
                <span>
                  {errorMetric && errorMetric.value !== null ? `${errorMetric.value}%` : '--'}
                </span>
              </div>
              <Progress
                value={
                  errorMetric?.value !== null && errorMetric?.value !== undefined && errorMetric.threshold ?
                    Math.min((errorMetric.value / errorMetric.threshold) * 100, 100) : 0
                }
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SystemHealthMonitor;

